import { Link } from 'react-router-dom';
import { LegalLayout } from './LegalLayout';
import { COMPANY, LEGAL_ROUTES } from './legal';

export function PrivacyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      active="privacy"
      intro="How we collect, use, share, and protect personal information, and the rights available to you under GDPR, Quebec Law 25, PIPEDA, and the CCPA/CPRA."
    >
      <h2>1. About this policy</h2>
      <p>
        Revvly is a subscription and revenue management platform. Businesses use Revvly to manage
        billing, subscriptions, payments, and revenue for their own clients. This policy explains what
        personal information we collect, how and why we use it, who we share it with, how long we keep
        it, and the rights available to you under applicable data protection laws — including the EU
        and UK General Data Protection Regulation (&ldquo;<strong>GDPR</strong>&rdquo;), Quebec&rsquo;s{' '}
        <em>Act respecting the protection of personal information in the private sector</em> (as
        amended by Law 25, &ldquo;<strong>Quebec Law 25</strong>&rdquo;) and Canada&rsquo;s{' '}
        <em>Personal Information Protection and Electronic Documents Act</em> (&ldquo;
        <strong>PIPEDA</strong>&rdquo;), and the California Consumer Privacy Act as amended by the
        California Privacy Rights Act (&ldquo;<strong>CCPA/CPRA</strong>&rdquo;).
      </p>
      <p>
        Please read Section 3 first — <strong>who this policy applies to</strong> — because our role,
        and therefore our obligations to you, depends on how you interact with Revvly.
      </p>

      <h2>2. Who we are</h2>
      <p>
        Revvly is operated by <strong>{COMPANY.legalName}</strong>, a company incorporated in{' '}
        {COMPANY.jurisdiction} ({COMPANY.companyNumber}) (&ldquo;<strong>Revvly</strong>,&rdquo;
        &ldquo;<strong>we</strong>,&rdquo; &ldquo;<strong>us</strong>&rdquo;), which acts as the{' '}
        <strong>data controller</strong> for the processing described in this policy.
      </p>
      <p>
        <strong>Contact for privacy matters:</strong>{' '}
        <a href={`mailto:${COMPANY.privacyEmail}`}>{COMPANY.privacyEmail}</a> · attn: Privacy Officer.
      </p>

      <h2>3. Who this policy applies to</h2>
      <p>
        Revvly is a business-to-business platform, and there are two distinct groups of individuals
        whose personal information may be processed. <strong>Our role differs for each.</strong>
      </p>
      <h3>3.1 Business Users (our customers)</h3>
      <p>
        &ldquo;<strong>Business Users</strong>&rdquo; are the businesses that subscribe to Revvly, and
        the individual staff members who create accounts and use the platform to manage their clients.
      </p>
      <p>
        For Business User personal information, <strong>Revvly is the controller.</strong> This policy
        governs that relationship in full, and the rights described in Section 10 are exercised against
        us.
      </p>
      <h3>3.2 Clients (our customers&rsquo; clients)</h3>
      <p>
        &ldquo;<strong>Clients</strong>&rdquo; are the customers, members, or payers of a Business
        User, whose information the Business User enters into and manages within Revvly — for example,
        to bill them, manage their subscription, or record a payment.{' '}
        <strong>Clients do not have Revvly accounts and do not use the platform.</strong> A Client may,
        however, interact with a payment page hosted by our payment processor (see Section 6) when
        paying a Business User.
      </p>
      <p>
        For Client personal information, <strong>the Business User is the controller and Revvly acts
        as a processor / service provider</strong>, processing that information solely on the Business
        User&rsquo;s documented instructions and under our data processing agreement with that Business
        User. We do not decide the purposes for which Client data is used, we do not use it for our own
        purposes, and we do not sell or share it.
      </p>
      <p>
        <strong>If you are a Client:</strong> the privacy policy of the Business User you deal with
        governs your relationship, and you should direct rights requests to that Business User. Section
        4.2 describes, at a high level, the categories of Client data we process on Business
        Users&rsquo; behalf, and Section 10 explains how we support Business Users in fulfilling your
        requests.
      </p>

      <h2>4. Information we collect</h2>
      <h3>4.1 Business User information (we are controller)</h3>
      <div className="legal__table-wrap">
        <table className="legal__table">
          <thead>
            <tr>
              <th>Category</th>
              <th>Examples</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <strong>Account &amp; identity data</strong>
              </td>
              <td>
                Name, work email, job title/role, business name, telephone number, profile settings
              </td>
            </tr>
            <tr>
              <td>
                <strong>Authentication data</strong>
              </td>
              <td>
                Login credentials (hashed), authentication tokens, multi-factor settings, session
                records
              </td>
            </tr>
            <tr>
              <td>
                <strong>Billing data</strong>
              </td>
              <td>
                Billing contact, billing address, subscription plan and status, invoices, payment
                method reference (see Section 6 — full card details are handled by our payment
                processor, not stored by us)
              </td>
            </tr>
            <tr>
              <td>
                <strong>Usage &amp; technical data</strong>
              </td>
              <td>
                IP address, device and browser type, operating system, pages and features accessed,
                actions taken, timestamps, referring URLs, diagnostic and log data
              </td>
            </tr>
            <tr>
              <td>
                <strong>Product usage data</strong>
              </td>
              <td>
                Which pages of the Revvly app you open (with record numbers removed from the
                address), how long each stays in view, a session identifier, and timestamps,
                linked to your work email address and to your organization. See Section 4.1.1
              </td>
            </tr>
            <tr>
              <td>
                <strong>Communications</strong>
              </td>
              <td>Support requests, correspondence with us, feedback, and survey responses</td>
            </tr>
            <tr>
              <td>
                <strong>Cookies &amp; similar technologies</strong>
              </td>
              <td>
                See Section 5 and our <Link to={LEGAL_ROUTES.cookies}>Cookie Policy</Link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        We collect this information directly from you when you register, configure, and use Revvly;
        automatically as you use the platform; and occasionally from your organization&rsquo;s
        administrator when they provision your account.
      </p>
      <h3>4.1.1 How we measure use of Revvly</h3>
      <p>
        While you are signed in, the Revvly app records which pages you open and for how long each
        stays in view, so that we can see how each Business User&rsquo;s organization uses Revvly. We
        use this to understand which features are used, to support our customers (for example, to see
        whether a team has got started), and to improve the product.
      </p>
      <ul>
        <li>
          <strong>What is recorded.</strong> The page you opened, shown as a pattern (for example{' '}
          <code>/clients/:id</code>, never the specific record), the seconds the page was in view,
          when it happened, and a session identifier that changes after 30 minutes of inactivity.
          Each record is linked to your work email address and to your organization.
        </li>
        <li>
          <strong>What is not recorded.</strong> The content of any page, anything you type or
          enter, search terms, the web address&rsquo;s query string or fragment, page titles, or
          Client data.
        </li>
        <li>
          <strong>Who sees it.</strong> Only Revvly personnel, in our own systems. It is not shown to
          other users of your organization, is not sold, and is not sent to analytics or advertising
          providers. It is separate from the website analytics described in Section 5.
        </li>
        <li>
          <strong>Who is left out.</strong> People who use Revvly as members of our own team,
          including when they open a Business User&rsquo;s workspace to help.
        </li>
        <li>
          <strong>Your choices.</strong> If your browser sends a Do Not Track signal, nothing is
          recorded. You can object to this processing, or ask us to delete the records linked to you,
          by writing to <a href={`mailto:${COMPANY.privacyEmail}`}>{COMPANY.privacyEmail}</a>.
        </li>
        <li>
          <strong>How long.</strong> Records are deleted automatically 13 months after they are
          received.
        </li>
      </ul>
      <h3>4.2 Client information (we are processor on the Business User&rsquo;s behalf)</h3>
      <p>
        On the instructions of each Business User, we process Client data that the Business User
        enters or generates in Revvly, such as: name and contact details; subscription and plan
        records; billing amounts, currency, and payment status; invoices and transaction history; and
        payment metadata returned by our payment processor (such as the payment card&rsquo;s last four
        digits, brand, and expiry, and the processor&rsquo;s customer/subscription identifiers). We do{' '}
        <strong>not</strong> receive or store full payment card numbers — see Section 6. Clients do not
        hold accounts and do not log in to Revvly.
      </p>
      <p>
        The precise categories are determined by the Business User&rsquo;s own configuration and
        privacy policy.
      </p>

      <h2>5. Cookies and similar technologies</h2>
      <p>
        We use strictly necessary cookies to operate Revvly (for example, to keep Business Users
        logged in and to secure the service), and, where required, we obtain consent for analytics or
        preference cookies. You can manage non-essential cookies through the cookie banner or your
        browser settings. Our <Link to={LEGAL_ROUTES.cookies}>Cookie Policy</Link> describes each
        cookie we use, its purpose, and its duration.
      </p>
      <p>
        Under Quebec Law 25, where we use technology that allows you to be identified, located, or
        profiled, we will inform you and provide the means to activate the functions that de-identify
        you where such technology is enabled by default.
      </p>

      <h2>6. Payment processing</h2>
      <p>
        Payments in Revvly are processed through our third-party payment processor,{' '}
        <strong>Stripe</strong>. When a Client pays a Business User, or a Business User pays for its
        Revvly subscription:
      </p>
      <ul>
        <li>
          Payment card and bank details are transmitted directly to Stripe and are handled within
          Stripe&rsquo;s PCI-DSS-compliant environment.{' '}
          <strong>We do not receive or store full card numbers.</strong>
        </li>
        <li>
          We receive and store payment metadata (status, amount, currency, last four digits, card
          brand, and processor identifiers) to manage subscriptions, invoicing, and reconciliation.
        </li>
        <li>
          Stripe processes payment data as an independent controller for its own compliance and
          fraud-prevention purposes, and as a processor for the transaction itself. Stripe&rsquo;s
          handling of personal information is governed by the{' '}
          <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
            Stripe Privacy Policy
          </a>
          .
        </li>
      </ul>

      <h2>7. How we use Business User information and our legal bases</h2>
      <p>
        We use Business User personal information to provide and secure Revvly, to process your
        subscription, to provide support, to improve the product, to communicate service and
        administrative messages, to detect and prevent fraud and abuse, and to comply with our legal
        obligations.
      </p>
      <p>Under GDPR, we rely on the following legal bases:</p>
      <div className="legal__table-wrap">
        <table className="legal__table">
          <thead>
            <tr>
              <th>Purpose</th>
              <th>Legal basis (GDPR Art. 6)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Providing Revvly and performing our contract with the Business User</td>
              <td>Performance of a contract</td>
            </tr>
            <tr>
              <td>Billing a Business User for its Revvly subscription</td>
              <td>Performance of a contract</td>
            </tr>
            <tr>
              <td>
                Securing the platform, preventing fraud/abuse, product improvement (including
                measuring how Revvly is used, Section 4.1.1), business administration
              </td>
              <td>Legitimate interests (Art. 6(1)(f)) — balanced against your rights</td>
            </tr>
            <tr>
              <td>Service, security, and administrative communications</td>
              <td>Legitimate interests / performance of a contract</td>
            </tr>
            <tr>
              <td>Optional marketing communications</td>
              <td>Consent (Art. 6(1)(a)), withdrawable at any time</td>
            </tr>
            <tr>
              <td>Meeting legal, tax, accounting, and regulatory requirements</td>
              <td>Legal obligation (Art. 6(1)(c))</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        Under Quebec Law 25 and PIPEDA, we rely on your consent (express or implied by the context)
        and on the other bases those laws permit, and we limit collection to what is necessary for the
        purposes above.
      </p>
      <p>
        We do not use Business User or Client personal information to make decisions producing legal
        or similarly significant effects based solely on automated processing without human
        involvement. If this ever changes, we will inform you in advance, explain the logic involved,
        and provide the opportunity to submit observations and request human review, as required by
        GDPR Art. 22 and Quebec Law 25.
      </p>

      <h2>8. How we share information</h2>
      <p>We share personal information only as described below, and never sell it:</p>
      <ul>
        <li>
          <strong>Sub-processors and service providers</strong> — the current list is set out on our{' '}
          <Link to={LEGAL_ROUTES.subprocessors}>Sub-processors</Link> page. Each is bound by contract
          to protect personal information and to process it only for the purposes we specify.
          Questions can be directed to{' '}
          <a href={`mailto:${COMPANY.privacyEmail}`}>{COMPANY.privacyEmail}</a>.
        </li>
        <li>
          <strong>Business Users</strong> — for Client data, we make information available to the
          relevant Business User, who is its controller.
        </li>
        <li>
          <strong>Legal and regulatory disclosures</strong> — where required by law, court order, or a
          valid request from a public authority, or to protect our rights, safety, and property or
          those of others.
        </li>
        <li>
          <strong>Corporate transactions</strong> — in connection with a merger, acquisition,
          financing, or sale of assets, subject to appropriate confidentiality protections.
        </li>
      </ul>

      <h2>9. International data transfers</h2>
      <p>
        Revvly and the service providers we use may process personal information in countries other
        than your own, including the United States, the EEA, and the United Kingdom.
      </p>
      <p>
        Where we transfer personal information across borders, we implement safeguards required by
        applicable law, including:
      </p>
      <ul>
        <li>
          <strong>From the EEA/UK:</strong> the European Commission&rsquo;s{' '}
          <strong>Standard Contractual Clauses</strong> (and the UK International Data Transfer
          Addendum), together with supplementary technical and organizational measures where
          necessary.
        </li>
        <li>
          <strong>From Quebec:</strong> before transferring personal information outside Quebec, we
          conduct a <strong>privacy impact assessment (PIA/TIA)</strong> as required by Law 25, and we
          ensure the information receives adequate protection through contractual and technical means.
        </li>
      </ul>
      <p>A copy of the relevant transfer mechanism is available on request.</p>

      <h2>10. Your rights</h2>
      <p>
        The rights available to you depend on where you live. To exercise any right, contact us at{' '}
        <a href={`mailto:${COMPANY.privacyEmail}`}>{COMPANY.privacyEmail}</a>. We will respond within
        the timeframes set by applicable law, and we may need to verify your identity before acting.{' '}
        <strong>If you are a Client, please contact the relevant Business User</strong>; where you
        contact us, we will forward the request to the Business User and assist them in responding.
      </p>
      <h3>10.1 GDPR (EU / UK)</h3>
      <p>
        You have the right to: access your personal information; have it corrected; have it erased;
        restrict or object to its processing; data portability; and, where processing is based on
        consent, to withdraw that consent at any time without affecting prior processing. You may also
        lodge a complaint with your supervisory authority (in the EU, your national data protection
        authority; in the UK, the Information Commissioner&rsquo;s Office at ico.org.uk).
      </p>
      <h3>10.2 Quebec Law 25 and PIPEDA (Canada)</h3>
      <p>
        You have the right to: access and receive a copy of your personal information; request its
        correction; withdraw consent; <strong>data portability</strong> (to receive computerized
        personal information in a structured, commonly used technological format); request that we
        cease disseminating your information or de-index a link where dissemination causes serious
        injury (the right to de-indexation); and be informed of, and make representations about, any
        automated processing. You may file a complaint with the{' '}
        <strong>Commission d&rsquo;accès à l&rsquo;information du Québec</strong> (cai.gouv.qc.ca) or,
        more broadly, the Office of the Privacy Commissioner of Canada (priv.gc.ca).
      </p>
      <p>
        <strong>
          Person responsible for the protection of personal information (Privacy Officer):
        </strong>{' '}
        reachable at <a href={`mailto:${COMPANY.privacyEmail}`}>{COMPANY.privacyEmail}</a>.
      </p>
      <h3>10.3 California (CCPA/CPRA)</h3>
      <p>
        If you are a California resident, you have the right to: <strong>know</strong> the categories
        and specific pieces of personal information we have collected, the sources, the purposes, and
        the categories of recipients; <strong>delete</strong> personal information we hold about you;{' '}
        <strong>correct</strong> inaccurate personal information; <strong>opt out</strong> of any sale
        or sharing of personal information; and <strong>limit</strong> the use and disclosure of
        sensitive personal information. You also have the right{' '}
        <strong>not to be discriminated against</strong> for exercising these rights.
      </p>
      <p>
        Over the preceding 12 months we have collected the categories of personal information
        described in Section 4 (identifiers, commercial information, internet/network activity, and
        professional information) for the business purposes described in Section 7.{' '}
        <strong>
          We do not sell your personal information, and we do not share it for cross-context
          behavioral advertising
        </strong>
        , as those terms are defined under the CCPA/CPRA. We therefore do not offer a distinct
        &ldquo;Do Not Sell or Share&rdquo; mechanism beyond honoring opt-out preference signals where
        applicable; if this ever changes, we will update this policy and provide the required opt-out.
      </p>
      <p>
        You may use an <strong>authorized agent</strong> to submit requests on your behalf, subject to
        verification.
      </p>

      <h2>11. Data retention</h2>
      <p>
        We retain personal information only for as long as necessary to fulfil the purposes set out in
        this policy, including to provide Revvly, to comply with legal, tax, and accounting
        obligations, to resolve disputes, and to enforce our agreements. When information is no longer
        needed, we delete or anonymize it. Product usage data (Section 4.1.1) is deleted
        automatically 13 months after it is received. Client data processed on a Business User&rsquo;s behalf is
        retained according to that Business User&rsquo;s instructions and our data processing
        agreement, and is returned or deleted on termination of the Business User&rsquo;s service,
        subject to legal retention requirements.
      </p>

      <h2>12. Security</h2>
      <p>
        We maintain technical and organizational measures designed to protect personal information
        against unauthorized access, loss, misuse, and alteration — including encryption in transit
        and at rest, access controls, and logging and monitoring. No system is perfectly secure, and
        we will notify you and the relevant authorities of a personal data breach where required by
        law.
      </p>

      <h2>13. Children&rsquo;s privacy</h2>
      <p>
        Revvly is a business tool and is not directed to children. We do not knowingly collect
        personal information from children under the age of 16 (or the applicable age of digital
        consent in your jurisdiction). If you believe a child has provided us personal information,
        contact us and we will delete it.
      </p>

      <h2>14. Changes to this policy</h2>
      <p>
        We may update this policy from time to time. When we make material changes, we will update the
        &ldquo;Last updated&rdquo; date above and, where appropriate, notify Business Users through the
        platform or by email. Your continued use of Revvly after an update constitutes acceptance of
        the revised policy where permitted by law.
      </p>

      <h2>15. Contact us</h2>
      <p>For any question about this policy or to exercise your rights:</p>
      <div className="legal__contact">
        <p>
          <strong>{COMPANY.legalName}</strong> — Privacy Officer ({COMPANY.companyNumber})
        </p>
        <p>
          Email: <a href={`mailto:${COMPANY.privacyEmail}`}>{COMPANY.privacyEmail}</a>
        </p>
      </div>
    </LegalLayout>
  );
}
